import { buildRouteSequence, type FlowSequenceContext } from './flow-sequence';
import type { SignupStep } from './steps';

export type StepGuardContext = FlowSequenceContext & {
  /** The funnel has an email once the entry step submitted successfully. */
  hasEmail: boolean;
};

/**
 * Funnel-integrity guards, ported from the Ember signup route's
 * `setupController` bounces and tightened to one contract: a step renders only
 * if the active `buildRouteSequence` contains it. This is the invariant
 * `getNextStep` relies on ("null when `current` isn't in the sequence — the
 * guards bounce those before navigation ever asks"): without it, history
 * back/forward could land on a step the visitor's answers have since removed
 * (e.g. `plan_choice` after use-case answers changed, `confirmation` for a
 * Google signup), leaving the funnel with no defined next step. Sequence
 * membership also subsumes Ember's individual rules — `onboarding_personalize`
 * is in no consolidated sequence, and startup signups' sequence omits
 * `company`.
 *
 * Flow state lives in memory (there is no draft persistence), so a deep link
 * or refresh past the entry step has nothing to work with — every guard lands
 * on `index`.
 *
 * Returns the step to render: `step` itself when allowed, or the bounce
 * target. The provider applies this before rendering and navigates when it
 * differs.
 */
export function resolveGuardedStep(step: SignupStep, context: StepGuardContext): SignupStep {
  // Any step past entry needs the in-memory flow state seeded by the entry
  // step's submit.
  if (step !== 'index' && !context.hasEmail) {
    return 'index';
  }

  if (!buildRouteSequence(context).includes(step)) {
    return 'index';
  }

  return step;
}
